import { useCallback, useMemo } from 'react';
import { endOfMonth, getWeeksInMonth, parseDate } from '@internationalized/date';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import { useCalendarGrid } from 'react-aria';
import type { DatePickerPagination } from '../../util';
import { CalendarGridCell } from './cell/calendar-grid-cell';
import styles from './calendar-grid.module.scss';

interface CalendarGridProps {
  locale: string;
  pagination?: DatePickerPagination;
  state: CalendarState | RangeCalendarState;
}

export function CalendarGrid({ locale, pagination, state }: CalendarGridProps) {
  const { lastAvailableDate, lastAvailableDateDirection = 'before', isFetchingNextPage, fetchNextPage, hasNextPage } = pagination || {};
  const startDate = useMemo(() => state.visibleRange.start.copy(), [state.visibleRange.start]);
  const endDate = useMemo(() => endOfMonth(startDate), [startDate]);
  const { gridProps, headerProps, weekDays } = useCalendarGrid({ startDate, endDate }, state);
  const weekRows = useMemo(() => {
    const weeksInMonth = getWeeksInMonth(startDate, locale);
    return new Array(weeksInMonth).fill(undefined).map((_, i) => i);
  }, [startDate, locale]);
  const lastAvailableDateAsDate = useMemo(() => {
    if (!lastAvailableDate) {
      return undefined;
    }

    return parseDate((typeof lastAvailableDate === 'string' ? lastAvailableDate : lastAvailableDate.toISOString()).split('T')[0]);
  }, [lastAvailableDate]);

  const handleGetNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return (
    <table className={styles.calendarGrid} {...gridProps}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, i) => (
            <th className={styles.weekDayHeading} key={`${day}-${i}`}>
              <span className={styles.content}>{day}</span>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {weekRows.map((weekIndex) => (
          <tr className={styles.weekRow} key={weekIndex}>
            {state.getDatesInWeek(weekIndex, startDate).map((date, i) => {
              if (!date) {
                return <td key={`${weekIndex}-day-${i}`} />;
              }

              const dateCompare = lastAvailableDateAsDate ? date.compare(lastAvailableDateAsDate) : null;
              const shouldActivatePagination =
                dateCompare !== null &&
                (dateCompare === 0 ||
                  (lastAvailableDateDirection === 'after' && dateCompare > 0) ||
                  (lastAvailableDateDirection === 'before' && dateCompare < 0));

              return (
                <CalendarGridCell
                  currentMonth={startDate}
                  date={date}
                  key={date.toDate(state.timeZone).toDateString()}
                  onVisible={shouldActivatePagination ? handleGetNextPage : undefined}
                  state={state}
                  timeZone={state.timeZone}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
